import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  Bot,
  Building2,
  Cable,
  CalendarDays,
  CheckCircle2,
  Clock3,
  FileSpreadsheet,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users,
  Workflow,
  XCircle
} from "lucide-react";
import {
  approveVioOperatorJob,
  cancelVioOperatorJob,
  createVioOperatorDraft,
  getVioOperatorLoginPassword,
  getVioOperatorOverview,
  markVioOperatorJobPrepared,
  syncVioOperatorSnapshot,
  testVioOperatorConnection,
  verifyVioOperatorJob
} from "../services/vioOperatorService";
import {
  collectVioPortalSnapshot,
  detectVioPortalBridge,
  prepareVioPortalJob,
  verifyVioPortalJob as collectVioPortalJobVerification
} from "../services/vioPortalBridge";
import { subscribeMonthlyFinancialReports } from "../services/monthlyFinancialReportService";
import MonthlyFinancialReportsView from "./MonthlyFinancialReportsView";

const latestPeriod = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const formatPeriod = (value) => {
  if (!/^20\d{2}-(0[1-9]|1[0-2])$/.test(value || "")) return value || "Dönem yok";
  const [year, month] = value.split("-").map(Number);
  return new Intl.DateTimeFormat("tr-TR", { month: "long", year: "numeric" })
    .format(new Date(year, month - 1, 1));
};

const normalizeCompanies = (companyOptions = []) => companyOptions
  .map((company) => {
    const name = company.firmaAdi || company.legalName || company.name || "";
    const id = company.id || name
      .toLocaleLowerCase("tr-TR")
      .replace(/[ıİ]/g, "i")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    return {
      id,
      name,
      shortName: company.firmaKisaAdi || company.shortName || name
    };
  })
  .filter((company) => company.id && company.name);

const getErrorMessage = (error, fallback) => {
  const message = String(error?.message || "").replace(/^Firebase:\s*/i, "");
  return message || fallback;
};

const loadOneTimePortalLogin = async (tenantId, connection) => {
  const password = await getVioOperatorLoginPassword(tenantId, connection.id);
  if (!connection.username || !password) {
    throw new Error("ARGEPORTAL için kayıtlı kullanıcı adı veya parola bulunamadı.");
  }
  return { username: connection.username, password };
};

const clearOneTimePortalLogin = (login) => {
  if (!login) return;
  login.username = "";
  login.password = "";
};

const MINIMUM_BRIDGE_VERSION = "0.12.0";
const compareVersions = (left, right) => {
  const leftParts = String(left || "0").split(".").map(Number);
  const rightParts = String(right || "0").split(".").map(Number);
  for (let index = 0; index < Math.max(leftParts.length, rightParts.length); index += 1) {
    const difference = (leftParts[index] || 0) - (rightParts[index] || 0);
    if (difference) return difference;
  }
  return 0;
};

const STATUS_META = {
  awaiting_approval: { label: "Onay bekliyor", tone: "amber", icon: Clock3 },
  approved: { label: "Portalda hazırlanmaya hazır", tone: "blue", icon: Cable },
  prepared: { label: "Portal kontrolü bekliyor", tone: "amber", icon: Clock3 },
  queued: { label: "Kuyrukta", tone: "blue", icon: Clock3 },
  running: { label: "Çalışıyor", tone: "mint", icon: Loader2 },
  completed: { label: "Tamamlandı", tone: "green", icon: CheckCircle2 },
  failed: { label: "İşlem gerekli", tone: "red", icon: AlertTriangle },
  cancelled: { label: "İptal edildi", tone: "slate", icon: XCircle }
};

function VioOperatorView({
  tenantId,
  user,
  userData,
  companyOptions = [],
  projectOptions = [],
  themeMode = "light",
  canEdit = true,
  onOpenPortalCredentials
}) {
  const [moduleName, setModuleName] = useState("dgs");
  const [overview, setOverview] = useState({ connections: [], jobs: [] });
  const [financialReports, setFinancialReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState("");
  const [message, setMessage] = useState(null);
  const [connectionId, setConnectionId] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [periodKey, setPeriodKey] = useState(latestPeriod);
  const [dgsProjectPortalId, setDgsProjectPortalId] = useState("");
  const [projectProgress, setProjectProgress] = useState("");
  const [financialReportId, setFinancialReportId] = useState("");
  const [bridgeStatus, setBridgeStatus] = useState("checking");
  const [bridgeVersion, setBridgeVersion] = useState("");
  const dark = themeMode === "dark";
  const companies = useMemo(() => normalizeCompanies(companyOptions), [companyOptions]);
  const bridgeNeedsUpdate = bridgeStatus === "ready" && compareVersions(bridgeVersion, MINIMUM_BRIDGE_VERSION) < 0;

  const refresh = async ({ silent = false } = {}) => {
    if (!tenantId) return;
    if (!silent) setLoading(true);
    try {
      const data = await getVioOperatorOverview(tenantId);
      setOverview({ connections: data.connections || [], jobs: data.jobs || [] });
      setConnectionId((current) => current || data.connections?.[0]?.id || "");
      setMessage((current) => current?.type === "error" ? null : current);
    } catch (error) {
      setMessage({ type: "error", text: getErrorMessage(error, "Vio Operatör bilgileri alınamadı.") });
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  useEffect(() => {
    detectVioPortalBridge()
      .then((result) => {
        setBridgeStatus("ready");
        setBridgeVersion(result?.version || "");
      })
      .catch(() => setBridgeStatus("missing"));
  }, []);

  useEffect(() => subscribeMonthlyFinancialReports({
    tenantId,
    onData: setFinancialReports,
    onError: () => setFinancialReports([])
  }), [tenantId]);

  useEffect(() => {
    setCompanyId((current) => current || companies[0]?.id || "");
  }, [companies]);

  const selectedConnection = overview.connections.find((item) => item.id === connectionId) || null;
  const portalSnapshot = selectedConnection?.snapshot || null;
  const portalProjects = useMemo(() => portalSnapshot?.projects || [], [portalSnapshot]);
  const dgsEligiblePersonnel = useMemo(() => portalSnapshot?.dgsPeriodKey === periodKey
    ? (portalSnapshot?.dgsEligiblePersonnel || [])
    : [], [portalSnapshot, periodKey]);
  const selectedDgsProject = portalProjects.find((item) => String(item.portalId || item.name) === String(dgsProjectPortalId)) || null;
  const selectedCompany = companies.find((item) => item.id === companyId) || null;
  const matchingFinancialReports = financialReports.filter((report) =>
    String(report.companyId || "") === String(companyId || "") &&
    String(report.periodKey || "") === String(periodKey || "")
  );
  const selectedFinancialReport = matchingFinancialReports.find((item) => item.id === financialReportId)
    || matchingFinancialReports[0]
    || null;

  useEffect(() => {
    setFinancialReportId(matchingFinancialReports[0]?.id || "");
    // Only reset when company or period changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, periodKey, financialReports]);

  useEffect(() => {
    if (!portalSnapshot) {
      setDgsProjectPortalId("");
      return;
    }
    setDgsProjectPortalId((current) => {
      if (portalProjects.some((item) => String(item.portalId || item.name) === String(current))) return current;
      return portalProjects[0]?.portalId || portalProjects[0]?.name || "";
    });
  }, [portalSnapshot, portalProjects]);

  const checkConnection = async () => {
    if (!connectionId) return;
    setBusyAction("connection");
    setMessage(null);
    try {
      const result = await testVioOperatorConnection(tenantId, connectionId);
      setMessage({
        type: result.reachable ? "success" : "error",
        text: result.reachable
          ? `Portal adresine güvenli bağlantı kuruldu (${result.hostname}). Kayıtlı şifre sunucuda doğrulandı.`
          : "Portal yanıt verdi ancak erişilebilir bir giriş sayfası doğrulanamadı."
      });
      await refresh({ silent: true });
    } catch (error) {
      setMessage({ type: "error", text: getErrorMessage(error, "Portal bağlantısı denetlenemedi.") });
    } finally {
      setBusyAction("");
    }
  };

  const syncPortal = async () => {
    if (!selectedConnection) return;
    if (bridgeStatus !== "ready" || bridgeNeedsUpdate) {
      setMessage({ type: "error", text: `Otomatik portal girişi için Vio Köprü ${MINIMUM_BRIDGE_VERSION} güncellemesini kurun.` });
      return;
    }
    setBusyAction("sync");
    setMessage(null);
    let login = null;
    try {
      login = await loadOneTimePortalLogin(tenantId, selectedConnection);
      const capturedSnapshot = await collectVioPortalSnapshot({
        portalUrl: selectedConnection.portalUrl,
        periodKey,
        scope: moduleName === "dgs" && portalSnapshot ? "dgs" : "full",
        login
      });
      const snapshot = capturedSnapshot.partial && portalSnapshot
        ? {
            ...portalSnapshot,
            ...capturedSnapshot,
            company: { ...(portalSnapshot.company || {}), ...(capturedSnapshot.company || {}) }
          }
        : capturedSnapshot;
      const result = await syncVioOperatorSnapshot(tenantId, selectedConnection.id, snapshot);
      const synced = result.snapshot || snapshot;
      setBridgeStatus("ready");
      setMessage({
        type: "success",
        text: `ARGEPORTAL verileri güncellendi: ${synced.projects?.length || 0} proje, ${synced.personnel?.length || 0} personel ve ${synced.dgsEligiblePersonnel?.length || 0} DGS bilişim personeli bulundu.`
      });
      await refresh({ silent: true });
    } catch (error) {
      const text = getErrorMessage(error, "ARGEPORTAL verileri senkronlanamadı.");
      if (/Vio Köprü (?:bulunamadı|bağlantısı kurulamadı)|Chrome köprüsü/i.test(text)) setBridgeStatus("missing");
      setMessage({ type: "error", text });
    } finally {
      clearOneTimePortalLogin(login);
      setBusyAction("");
    }
  };

  const createDraft = async () => {
    if (!selectedConnection || !selectedCompany || !periodKey) {
      setMessage({ type: "error", text: "Portal bağlantısı, firma ve dönem seçimi zorunludur." });
      return;
    }
    if (!portalSnapshot) {
      setMessage({ type: "error", text: "Önce açık ARGEPORTAL oturumundaki verileri senkronlayın." });
      return;
    }
    if (moduleName === "dgs" && (!dgsEligiblePersonnel.length || !selectedDgsProject)) {
      setMessage({ type: "error", text: "Önce bu dönem için portal verilerini yenileyin; DGS'ye gelen bilişim personelleri ve projeyi doğrulayın." });
      return;
    }
    if (moduleName === "project_progress" && (!selectedDgsProject || !projectProgress.trim())) {
      setMessage({ type: "error", text: "Proje ilerlemesi için proje seçimi ve dönem içinde yapılan çalışma özeti zorunludur." });
      return;
    }
    if (moduleName === "financial" && !selectedFinancialReport) {
      setMessage({
        type: "error",
        text: "Seçilen firma ve dönem için Aylık Mali Veriler kaydı bulunamadı. Önce Excel aktarın veya elle giriş yapın."
      });
      return;
    }

    setBusyAction("draft");
    setMessage(null);
    try {
      await createVioOperatorDraft(tenantId, {
        module: moduleName,
        connectionId,
        companyId: selectedCompany.id,
        companyName: selectedCompany.name,
        periodKey,
        periodLabel: formatPeriod(periodKey),
        projectProgress: moduleName === "project_progress" ? projectProgress : "",
        projectPortalId: ["dgs", "project_progress"].includes(moduleName)
          ? (selectedDgsProject.portalId || selectedDgsProject.name)
          : "",
        dgsProjectPortalId: moduleName === "dgs" ? (selectedDgsProject.portalId || selectedDgsProject.name) : "",
        financialReportId: moduleName === "financial" ? selectedFinancialReport.id : ""
      });
      setProjectProgress("");
      setMessage({
        type: "success",
        text: `${moduleName === "dgs" ? "DGS" : moduleName === "project_progress" ? "Proje ilerlemesi" : "Mali veri"} işlemi taslak olarak hazırlandı. Portalda işlem yapılması için ayrıca onay vermelisiniz.`
      });
      await refresh({ silent: true });
    } catch (error) {
      setMessage({ type: "error", text: getErrorMessage(error, "İşlem taslağı oluşturulamadı.") });
    } finally {
      setBusyAction("");
    }
  };

  const approveJob = async (job) => {
    const confirmed = await window.appConfirm(
      `${job.companyName} · ${job.periodLabel || formatPeriod(job.periodKey)} için hazırlanan işlem onaylanacak. Vio Operatör portal yürütücüsü devreye alındığında bu kayıt otomatik işleme alınacaktır.`,
      {
        title: "Portal işlemini onaylıyor musunuz?",
        type: "warning",
        confirmText: "Onayla"
      }
    );
    if (!confirmed) return;
    setBusyAction(job.id);
    try {
      await approveVioOperatorJob(tenantId, job.id);
      setMessage({ type: "success", text: "İşlem onaylandı ve güvenli yürütücü için hazırlandı." });
      await refresh({ silent: true });
    } catch (error) {
      setMessage({ type: "error", text: getErrorMessage(error, "İşlem onaylanamadı.") });
    } finally {
      setBusyAction("");
    }
  };

  const cancelJob = async (job) => {
    const confirmed = await window.appConfirm("Bu Vio Operatör taslağı iptal edilsin mi?", {
      title: "İşlemi iptal et",
      type: "danger",
      confirmText: "İptal Et"
    });
    if (!confirmed) return;
    setBusyAction(job.id);
    try {
      await cancelVioOperatorJob(tenantId, job.id);
      await refresh({ silent: true });
    } catch (error) {
      setMessage({ type: "error", text: getErrorMessage(error, "İşlem iptal edilemedi.") });
    } finally {
      setBusyAction("");
    }
  };

  const prepareJob = async (job) => {
    if (bridgeStatus !== "ready" || bridgeNeedsUpdate) {
      setMessage({ type: "error", text: `Sekmeyi otomatik açabilmek için Vio Köprü ${MINIMUM_BRIDGE_VERSION} güncellemesini kurun.` });
      return;
    }
    const connection = overview.connections.find((item) => item.id === job.connectionId);
    if (!connection) {
      setMessage({ type: "error", text: "İşlemin portal bağlantısı bulunamadı." });
      return;
    }
    const preparationMessage = job.module === "dgs"
      ? `ARGEPORTAL'da ${job.dgsSetup?.personnelCount || 0} bilişim personeli için DGS kayıtları oluşturulacak; her kayıtta Kaydet ve Onaya Gönder uygulanacak. Dönem, proje, 10766 uzaktan çalışma ve hafta içi 09:00–18:00 kuralını onaylıyor musunuz?`
      : job.module === "project_progress"
        ? `ARGEPORTAL'da ${job.projectSetup?.projectName || "seçilen proje"} için dönem özeti yazılacak, projeye gelen personeller seçilecek ve kayıt Kaydet ile tamamlanacak. Onaylıyor musunuz?`
        : `ARGEPORTAL'da mali veriler ${job.financialSetup?.expectedActionCount || 0} kontrollü kart işlemi olarak kaydedilecek. Tutar olmayan bölümler “Hareketim Yok” ile kapatılacak. Onaylıyor musunuz?`;
    const confirmed = await window.appConfirm(
      preparationMessage,
      {
        title: "Portal formu hazırlansın mı?",
        type: "warning",
        confirmText: job.module === "dgs" ? "Toplu Uygula" : job.module === "project_progress" ? "Kaydı Uygula" : "Mali Verileri Uygula"
      }
    );
    if (!confirmed) return;
    setBusyAction(job.id);
    setMessage(null);
    let login = null;
    try {
      login = await loadOneTimePortalLogin(tenantId, connection);
      const preparation = await prepareVioPortalJob({ portalUrl: connection.portalUrl, job, login });
      await markVioOperatorJobPrepared(tenantId, job.id, preparation);
      if (["project_progress", "financial"].includes(job.module)) {
        const verification = await collectVioPortalJobVerification({ portalUrl: connection.portalUrl, job, login });
        await verifyVioOperatorJob(tenantId, job.id, verification);
      }
      setMessage({
        type: "success",
        text: job.module === "dgs"
          ? `${preparation.preparedCount || job.dgsSetup?.personnelCount || 0} bilişim personelinin DGS kaydı hazırlandı (${preparation.submittedCount || 0} yeni, ${preparation.existingCount || 0} mevcut). “Kaydı Doğrula” ile sonucu kontrol edin.`
          : job.module === "project_progress"
            ? "Proje ilerleme bilgisi, dönem özeti ve projeye gelen personellerle birlikte kaydedilip doğrulandı."
            : `${preparation.preparedCount || 0} mali kart işlemi portalda kaydedilip doğrulandı.`
      });
      await refresh({ silent: true });
    } catch (error) {
      setMessage({ type: "error", text: getErrorMessage(error, "Portal formu hazırlanamadı.") });
    } finally {
      clearOneTimePortalLogin(login);
      setBusyAction("");
    }
  };

  const verifyJob = async (job) => {
    if (bridgeStatus !== "ready" || bridgeNeedsUpdate) {
      setMessage({ type: "error", text: `Kaydı doğrulayabilmek için Vio Köprü ${MINIMUM_BRIDGE_VERSION} güncellemesini kurun.` });
      return;
    }
    const connection = overview.connections.find((item) => item.id === job.connectionId);
    if (!connection) {
      setMessage({ type: "error", text: "İşlemin portal bağlantısı bulunamadı." });
      return;
    }
    setBusyAction(job.id);
    setMessage(null);
    let login = null;
    try {
      login = await loadOneTimePortalLogin(tenantId, connection);
      const verification = await collectVioPortalJobVerification({ portalUrl: connection.portalUrl, job, login });
      await verifyVioOperatorJob(tenantId, job.id, verification);
      setMessage({ type: "success", text: "Portal kaydı doğrulandı ve işlem tamamlandı." });
      await refresh({ silent: true });
    } catch (error) {
      setMessage({ type: "error", text: getErrorMessage(error, "Portal kaydı doğrulanamadı.") });
    } finally {
      clearOneTimePortalLogin(login);
      setBusyAction("");
    }
  };

  const counts = useMemo(() => ({
    approval: overview.jobs.filter((item) => item.status === "awaiting_approval").length,
    active: overview.jobs.filter((item) => ["approved", "prepared", "queued", "running"].includes(item.status)).length,
    completed: overview.jobs.filter((item) => item.status === "completed").length
  }), [overview.jobs]);

  if (loading) {
    return <div className="vio-operator-loading"><Loader2 className="is-spinning" /> Vio Operatör hazırlanıyor...</div>;
  }

  return (
    <div className={`vio-operator${dark ? " is-dark" : ""}`}>
      <section className="vio-operator-hero">
        <div className="vio-operator-orb"><Bot size={32} /></div>
        <div>
          <span className="vio-eyebrow"><Sparkles size={14} /> KONTROLLÜ PORTAL OTOMASYONU</span>
          <h1>Vio Operatör</h1>
          <p>Sizin onayınızla DGS, proje ilerleme ve mali veri işlemlerini hazırlayan dijital operasyon asistanı.</p>
        </div>
        <div className="vio-security-note"><ShieldCheck size={19} /><span>Parolanız yalnızca onaylı işlem anında çözülür, Vio Köprü tarafından portal girişinde tek kullanımlık işlenir; tarayıcıda saklanmaz ve işlem kayıtlarına yazılmaz.</span></div>
      </section>

      {message && <div className={`vio-message is-${message.type}`}>{message.type === "success" ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}<span>{message.text}</span></div>}

      <section className="vio-stat-grid">
        <Stat icon={Cable} label="Portal bağlantısı" value={overview.connections.length} detail={overview.connections.some((item) => item.isArgePortal) ? "ARGEPORTAL kaydı bulundu" : "Bağlantı seçilmeli"} />
        <Stat icon={Users} label="Portal verisi" value={(portalSnapshot?.personnel?.length || 0) + (portalSnapshot?.projects?.length || 0)} detail={portalSnapshot?.syncedAt ? `Son senkron ${new Date(portalSnapshot.syncedAt).toLocaleString("tr-TR")}` : "Henüz senkronlanmadı"} />
        <Stat icon={Clock3} label="Onay bekleyen" value={counts.approval} detail="Portalda henüz işlem yapılmadı" />
        <Stat icon={CheckCircle2} label="Tamamlanan" value={counts.completed} detail="Kanıtlı portal işlemi" />
      </section>

      <div className="vio-operator-layout">
        <section className="vio-panel vio-builder">
          <div className="vio-panel-heading">
            <div><span>Yeni işlem</span><h2>Operatör taslağı hazırlayın</h2></div>
            <div className="vio-module-switch" role="tablist" aria-label="Operatör modülü">
              <button type="button" className={moduleName === "dgs" ? "is-active" : ""} onClick={() => setModuleName("dgs")}><CalendarDays size={17} /> DGS</button>
              <button type="button" className={moduleName === "project_progress" ? "is-active" : ""} onClick={() => setModuleName("project_progress")}><TrendingUp size={17} /> Proje İlerlemesi</button>
              <button type="button" className={moduleName === "financial" ? "is-active" : ""} onClick={() => setModuleName("financial")}><FileSpreadsheet size={17} /> Mali Veri</button>
            </div>
          </div>

          {!overview.connections.length ? (
            <div className="vio-empty-state">
              <Cable size={28} />
              <h3>Portal bağlantısı bulunamadı</h3>
              <p>Portal Erişimleri alanına açıklaması “ARGEPORTAL” olan bir kayıt ekleyin.</p>
              <button type="button" onClick={onOpenPortalCredentials}>Portal Erişimlerine Git <ArrowRight size={16} /></button>
            </div>
          ) : (
            <div className="vio-form-grid">
              <label className="vio-field vio-field-wide">
                <span>Portal bağlantısı</span>
                <div className="vio-inline-control">
                  <select value={connectionId} onChange={(event) => setConnectionId(event.target.value)}>
                    {overview.connections.map((connection) => <option key={connection.id} value={connection.id}>{connection.isArgePortal ? "★ " : ""}{connection.technoparkName} · {connection.username}</option>)}
                  </select>
                  <div className="vio-connection-actions">
                    <button type="button" className="vio-secondary-button" disabled={busyAction === "connection"} onClick={checkConnection}>{busyAction === "connection" ? <Loader2 className="is-spinning" size={16} /> : <RefreshCw size={16} />} Denetle</button>
                    <button type="button" className="vio-sync-button" disabled={busyAction === "sync" || bridgeStatus === "checking" || bridgeNeedsUpdate} onClick={syncPortal}>{busyAction === "sync" ? <Loader2 className="is-spinning" size={16} /> : <Workflow size={16} />} Portal Verilerini Senkronla</button>
                  </div>
                </div>
                {selectedConnection && <small>{selectedConnection.portalUrl} {selectedConnection.isArgePortal && <b>· ARGEPORTAL</b>} {bridgeVersion && <b>· Vio Köprü v{bridgeVersion}</b>}</small>}
              </label>

              {(bridgeStatus === "missing" || bridgeNeedsUpdate) && (
                <div className="vio-bridge-notice vio-field-wide">
                  <Cable size={20} />
                  <div><strong>{bridgeNeedsUpdate ? `Vio Köprü ${MINIMUM_BRIDGE_VERSION} güncellemesi gerekiyor` : "Vio Köprü kurulumu gerekiyor"}</strong><span>Yeni paketi kurup Chrome uzantılarından yeniden yükleyin; ardından ARGEPORTAL ve Argevio sekmelerini yenileyin.</span></div>
                  <a href="/vio-operator-bridge.zip" download>{bridgeNeedsUpdate ? "Güncellemeyi indir" : "Vio Köprü'yü indir"}</a>
                </div>
              )}

              {portalSnapshot && (
                <div className="vio-snapshot-card vio-field-wide">
                  <div><CheckCircle2 size={19} /><span><strong>{portalSnapshot.company?.name || "ARGEPORTAL firması"}</strong><small>{portalSnapshot.syncedAt ? `${new Date(portalSnapshot.syncedAt).toLocaleString("tr-TR")} tarihinde senkronlandı` : "Senkronlandı"}</small></span></div>
                  <div className="vio-snapshot-counts"><span><b>{portalSnapshot.projects?.length || 0}</b> proje</span><span><b>{portalSnapshot.personnel?.length || 0}</b> personel</span><span><b>{portalSnapshot.periods?.length || 0}</b> dönem</span><span><b>{portalSnapshot.missingTasks?.length || 0}</b> eksik portal görevi</span></div>
                </div>
              )}

              <label className="vio-field">
                <span>Firma</span>
                <div className="vio-input-with-icon"><Building2 size={17} /><select value={companyId} onChange={(event) => setCompanyId(event.target.value)}><option value="">Firma seçin</option>{companies.map((company) => <option key={company.id} value={company.id}>{company.shortName}</option>)}</select></div>
              </label>
              <label className="vio-field">
                <span>Dönem</span>
                <div className="vio-input-with-icon"><CalendarDays size={17} /><input type="month" value={periodKey} onChange={(event) => setPeriodKey(event.target.value)} /></div>
              </label>

              {moduleName === "dgs" ? (
                <>
                  {portalSnapshot && (
                    <div className="vio-portal-source vio-field-wide">
                      <span>Seçilen dönem için portal hazırlığı</span>
                      <div><b>{portalSnapshot.projects?.length || 0}</b><small>eşleştirilebilir proje</small></div>
                      <div><b>{dgsEligiblePersonnel.length}</b><small>DGS bilişim personeli</small></div>
                      <div><b>{portalSnapshot.dgsRecords?.filter((item) => String(item.periodLabel || "").toLocaleLowerCase("tr-TR") === formatPeriod(periodKey).toLocaleLowerCase("tr-TR")).length || 0}</b><small>mevcut DGS kaydı</small></div>
                    </div>
                  )}
                  <div className="vio-field">
                    <span>Toplu personel kapsamı</span>
                    <div className="vio-personnel-batch">
                      <strong>{dgsEligiblePersonnel.length} bilişim personeli</strong>
                      <span>{dgsEligiblePersonnel.length ? dgsEligiblePersonnel.map((person) => person.name || person.portalLabel).join(", ") : "Dönemi seçip Portal Verilerini Senkronla düğmesine basın."}</span>
                    </div>
                  </div>
                  <label className="vio-field">
                    <span>Proje</span>
                    <div className="vio-input-with-icon"><Building2 size={17} /><select value={dgsProjectPortalId} onChange={(event) => setDgsProjectPortalId(event.target.value)}><option value="">Proje seçin</option>{portalProjects.map((project) => <option key={project.portalId || project.name} value={project.portalId || project.name}>{project.name || project.code}</option>)}</select></div>
                  </label>
                  <div className="vio-dgs-rule vio-field-wide">
                    <div><strong>Görevlendirme</strong><span>Diğer · 10766 Sy. Cumhurbaşkanı Kararı ile Uzaktan Çalışma</span></div>
                    <div><strong>Çalışma düzeni</strong><span>Hafta içi · 09:00–18:00 · Cumartesi/Pazar hariç</span></div>
                  </div>
                </>
              ) : moduleName === "project_progress" ? (
                <>
                  <div className="vio-portal-source vio-field-wide">
                    <span>Bağımsız proje ilerleme kaydı</span>
                    <div><b>{portalProjects.length}</b><small>eşleştirilebilir proje</small></div>
                    <div><b>{portalSnapshot?.progressRecords?.filter((item) => String(item.periodLabel || "").toLocaleLowerCase("tr-TR") === formatPeriod(periodKey).toLocaleLowerCase("tr-TR")).length || 0}</b><small>mevcut ilerleme kaydı</small></div>
                  </div>
                  <label className="vio-field vio-field-wide">
                    <span>Proje</span>
                    <div className="vio-input-with-icon"><Building2 size={17} /><select value={dgsProjectPortalId} onChange={(event) => setDgsProjectPortalId(event.target.value)}><option value="">Proje seçin</option>{portalProjects.map((project) => <option key={project.portalId || project.name} value={project.portalId || project.name}>{project.name || project.code}</option>)}</select></div>
                  </label>
                  <label className="vio-field vio-field-wide">
                    <span>Dönem İçinde Yapılan Çalışma</span>
                    <textarea rows="6" value={projectProgress} onChange={(event) => setProjectProgress(event.target.value)} placeholder="Seçilen dönemde yürütülen Ar-Ge çalışmalarını ve proje ilerlemelerini yazın..." />
                    <small>Portalda dönem ve proje seçilir; gelen proje personellerinin işaretleri kontrol edilir ve eksik olanlar seçilerek kayıt tamamlanır.</small>
                  </label>
                </>
              ) : (
                <div className="vio-field vio-field-wide">
                  <span>Aylık mali veri kaydı</span>
                  {selectedFinancialReport ? (
                    <div className="vio-report-card"><FileSpreadsheet size={22} /><div><strong>{selectedFinancialReport.companyShortName || selectedCompany?.shortName} · {formatPeriod(selectedFinancialReport.periodKey)}</strong><small>{selectedFinancialReport.rowCount || 0} veri satırı · {selectedFinancialReport.projects?.length || 0} proje · taslak oluşturmaya hazır</small></div><CheckCircle2 size={20} /></div>
                  ) : (
                    <div className="vio-warning-card"><AlertTriangle size={20} /><span>Bu firma ve dönem için kayıtlı mali veri bulunmuyor. Aşağıdan Excel yükleyin veya sistem projelerini seçerek elle hazırlayın.</span></div>
                  )}
                  <div style={{ marginTop: 16 }}>
                    <MonthlyFinancialReportsView
                      tenantId={tenantId}
                      user={user}
                      userData={userData}
                      companyOptions={companyOptions}
                      projectOptions={projectOptions}
                      themeMode={themeMode}
                      canEdit={canEdit}
                      mode="entry"
                      embedded
                      lockedCompanyId={companyId}
                      lockedPeriodKey={periodKey}
                    />
                  </div>
                </div>
              )}

              <div className="vio-approval-strip vio-field-wide"><ShieldCheck size={20} /><div><strong>Önce taslak, sonra onay</strong><span>Bu düğme portalda işlem yapmaz. Hazırlanan kayıt ayrıca sizin onayınıza sunulur.</span></div></div>
              <button type="button" className="vio-primary-button vio-field-wide" disabled={!canEdit || busyAction === "draft" || !portalSnapshot} onClick={createDraft}>{busyAction === "draft" ? <Loader2 className="is-spinning" size={18} /> : <Bot size={18} />} Operatör Taslağını Hazırla</button>
            </div>
          )}
        </section>

        <section className="vio-panel vio-queue">
          <div className="vio-panel-heading">
            <div><span>İşlem merkezi</span><h2>Onay ve yürütme kuyruğu</h2></div>
            <button type="button" className="vio-icon-button" onClick={() => refresh()} aria-label="Kuyruğu yenile"><RefreshCw size={17} /></button>
          </div>
          {!overview.jobs.length ? <div className="vio-empty-queue"><Bot size={30} /><strong>Henüz işlem taslağı yok</strong><span>İlk DGS, proje ilerleme veya mali veri taslağınızı soldaki alandan hazırlayın.</span></div> : (
            <div className="vio-job-list">
              {overview.jobs.map((job) => <OperatorJob key={job.id} job={job} busy={busyAction === job.id} onApprove={() => approveJob(job)} onPrepare={() => prepareJob(job)} onVerify={() => verifyJob(job)} onCancel={() => cancelJob(job)} />)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, detail }) {
  return <article className="vio-stat"><span><Icon size={20} /></span><div><small>{label}</small><strong>{value}</strong><p>{detail}</p></div></article>;
}

function OperatorJob({ job, busy, onApprove, onPrepare, onVerify, onCancel }) {
  const status = STATUS_META[job.status] || STATUS_META.awaiting_approval;
  const StatusIcon = status.icon;
  const JobIcon = job.module === "dgs" ? CalendarDays : job.module === "project_progress" ? TrendingUp : FileSpreadsheet;
  const jobLabel = job.module === "dgs" ? "DGS" : job.module === "project_progress" ? "Proje İlerlemesi" : "Mali Veri";
  return <article className="vio-job">
    <div className="vio-job-head">
      <span className={`vio-job-kind is-${job.module}`}><JobIcon size={15} />{jobLabel}</span>
      <span className={`vio-status is-${status.tone}`}><StatusIcon className={job.status === "running" ? "is-spinning" : ""} size={14} /> {status.label}</span>
    </div>
    <strong>{job.companyName}</strong>
    <p>{job.connectionName} · {job.periodLabel || formatPeriod(job.periodKey)}</p>
    {job.portalContext && <small className="vio-job-source">Portal verisi: {job.portalContext.projectCount || 0} proje · {job.portalContext.personnelCount || 0} personel</small>}
    {job.dgsSetup && <small className="vio-job-source">{job.dgsSetup.personnelCount || job.dgsSetup.personnel?.length || 0} bilişim personeli · {job.dgsSetup.projectName} · Hafta içi 09:00–18:00</small>}
    {job.projectSetup && <small className="vio-job-source">{job.projectSetup.projectName}</small>}
    {job.financialSetup && <small className="vio-job-source">{job.financialSetup.expectedActionCount || 0} mali kart işlemi</small>}
    {job.projectProgress && <blockquote>{job.projectProgress}</blockquote>}
    <div className="vio-job-footer"><small>{job.createdByName || "Kullanıcı"}{job.createdAt ? ` · ${new Date(job.createdAt).toLocaleString("tr-TR")}` : ""}</small><div>{["awaiting_approval", "approved", "prepared"].includes(job.status) && <button type="button" className="is-cancel" disabled={busy} onClick={onCancel}>İptal</button>}{job.status === "awaiting_approval" && <button type="button" className="is-approve" disabled={busy} onClick={onApprove}>{busy ? <Loader2 className="is-spinning" size={14} /> : <ShieldCheck size={14} />} Onayla</button>}{job.status === "approved" && <button type="button" className="is-prepare" disabled={busy} onClick={onPrepare}>{busy ? <Loader2 className="is-spinning" size={14} /> : <ArrowRight size={14} />} {job.module === "dgs" ? "Toplu Uygula" : job.module === "project_progress" ? "Kaydı Uygula" : "Mali Verileri Uygula"}</button>}{job.status === "prepared" && job.module === "dgs" && <button type="button" className="is-prepare" disabled={busy} onClick={onPrepare}>{busy ? <Loader2 className="is-spinning" size={14} /> : <ArrowRight size={14} />} Eksikleri Uygula</button>}{job.status === "prepared" && <button type="button" className="is-verify" disabled={busy} onClick={onVerify}>{busy ? <Loader2 className="is-spinning" size={14} /> : <ShieldCheck size={14} />} Kaydı Doğrula</button>}</div></div>
  </article>;
}

export default VioOperatorView;
